import { Spinner } from '@/ui/components/spinner';
import { focus } from '@/ui/focus';
import { cn } from '@/ui/utils';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, VariantProps } from 'cva';

const button = cva({
  base: [
    'touch-hitbox inline-flex cursor-pointer items-center justify-center whitespace-nowrap',
    'relative disabled:cursor-not-allowed data-pending:disabled:cursor-default disabled:opacity-75 transition [&_svg]:shrink-0',
    'data-pending:[-webkit-text-fill-color:transparent] data-pending:[&>*:not([data-slot=spinner])]:opacity-0',
    'data-indicated:after:block after:hidden after:absolute after:size-1.5 after:outline after:outline-white after:shadow-xs after:bg-primary-10 after:top-0 after:right-0 after:rounded-full',
    focus.base,
  ],
  variants: {
    variant: {
      primary:
        'bg-primary-9 text-white hover:bg-primary-9/90 shadow-sm border border-primary-11 active:bg-primary-9',
      secondary:
        'border hover:bg-surface-2 data-popup-open:bg-surface-2 shadow-xs bg-white active:bg-surface-3 data-selected:bg-surface-3 data-pressed:bg-surface-3',
      tertiary: 'hover:bg-surface-4 data-popup-open:bg-surface-4 bg-surface-3 active:bg-surface-4',
      'tertiary-2':
        'border hover:bg-surface-5 data-popup-open:bg-surface-6 shadow-xs bg-surface-4 active:bg-surface-6',
      ghost:
        'hover:bg-surface-3 not-data-inactive:aria-[current=page]:bg-surface-4 data-selected:bg-surface-4 data-popup-open:bg-surface-4 active:bg-surface-4 aria-expanded:bg-surface-4 data-selected:text-foreground',
      'ghost-accent':
        'hover:bg-primary-3 text-primary-10 aria-[current=page]:bg-primary-3 data-popup-open:bg-primary-3 active:bg-primary-3',
      'ghost-destructive':
        'hover:bg-red-3 hover:text-red-11 aria-[current=page]:bg-red-3 data-popup-open:bg-red-3 data-selected:text-red-11 data-selected:[&_svg:not([class*="text-"])]:fill-red-4',
      'ghost-warning':
        'hover:bg-amber-3 hover:text-amber-11 aria-[current=page]:bg-amber-3 data-popup-open:bg-amber-3 data-selected:text-amber-11 data-selected:[&_svg:not([class*="text-"])]:fill-amber-4',
      'ghost-success':
        'hover:bg-green-3 hover:text-green-11 aria-[current=page]:bg-green-3 data-popup-open:bg-green-3 data-selected:text-green-11 data-selected:[&_svg:not([class*="text-"])]:fill-green-4',
      dark: 'bg-foreground text-white hover:bg-foreground/90 shadow-sm active:bg-foreground',
      destructive:
        'bg-destructive text-white hover:bg-destructive/90 shadow-sm border border-red-11 active:bg-red-11',
      success: 'bg-success text-white hover:bg-success/90 shadow-sm border border-green-11',
      link: 'underline underline-offset-2 text-muted hover:text-foreground justify-start',
      'file-link': 'text-muted hover:text-foreground justify-start gap-1! p-0!',
      accent: 'text-blue-11 hover:text-blue-10 justify-start',
      unstyled: 'justify-start px-0!',
      'sidebar-item':
        'flex group/sidebar-link w-full justify-start [&>svg]:size-4 whitespace-nowrap [&>svg]:shrink-0 grow items-center gap-2! h-8 px-2! hover:bg-surface-3 active:bg-surface-4 not-data-inactive:aria-[current=page]:bg-surface-4 rounded-md text-sm!',
    },
    size: {
      '2xs': 'h-5 gap-1 px-1.5 text-xs [&_svg:not([class*="size-"])]:size-3.5 rounded-sm',
      xs: 'h-6 gap-1 px-2 text-xs [&_svg:not([class*="size-"])]:size-3.5 rounded-md',
      sm: 'h-7 gap-1.5 px-2.5 text-sm [&_svg:not([class*="size-"])]:size-4 rounded-md',
      md: 'h-8 gap-1.5 px-3 [&_svg:not([class*="size-"])]:size-4 rounded-md text-sm md:text-xs',
      lg: 'h-9 gap-2.5 px-3.5 text-sm [&_svg:not([class*="size-"])]:size-4.5 [&[role=combobox]]:[&_svg:not([class*="size-"])]:size-4 rounded-md',
      xl: 'h-10 gap-2 px-3 text-sm [&_svg:not([class*="size-"])]:size-5.5 rounded-lg',
    },
    kind: {
      default: '',
      icon: 'px-0! aspect-square w-auto justify-center',
      'with-icon': '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
    kind: 'default',
  },
  compoundVariants: [
    {
      variant: 'link',
      className: 'px-0 h-auto w-fit gap-1 rounded-none',
    },
    {
      variant: 'accent',
      className: 'px-0 h-auto w-fit gap-1 rounded-none',
    },
    {
      size: ['2xs', 'xs'],
      kind: 'icon',
      className: 'not-touch-hitbox',
    },
    {
      size: 'md',
      kind: 'icon',
      className: '[&_svg:not([class*="size-"])]:size-4.5',
    },
    {
      size: 'md',
      kind: ['default', 'with-icon'],
      className: 'aria-[haspopup=menu]:text-sm',
    },
    {
      size: 'xs',
      kind: 'with-icon',
      className:
        'gap-1.5 [&_svg:nth-child(1_of_:not([data-slot=spinner]))]:-mx-0.5 [&_svg:nth-last-child(1_of_:not([data-slot=spinner]))]:-mx-0.5',
    },
    {
      size: 'sm',
      kind: 'with-icon',
      className:
        'gap-2 [&_svg:nth-child(1_of_:not([data-slot=spinner]))]:-mx-0.75 [&_svg:nth-last-child(1_of_:not([data-slot=spinner]))]:-mx-0.75',
    },
    {
      size: 'md',
      kind: 'with-icon',
      className:
        'gap-2.5 [&_svg:nth-child(1_of_:not([data-slot=spinner]))]:-mx-0.75 [&_svg:nth-last-child(1_of_:not([data-slot=spinner]))]:-mx-0.75',
    },
    {
      size: 'lg',
      kind: 'with-icon',
      className:
        'gap-3 [&_svg:nth-child(1_of_:not([data-slot=spinner]))]:-mx-0.5 [&_svg:nth-last-child(1_of_:not([data-slot=spinner]))]:-mx-0.5',
    },
  ],
});

interface Props extends React.ComponentProps<'button'>, VariantProps<typeof button> {
  pending?: boolean | undefined;
  nativeButton?: ButtonPrimitive.Props['nativeButton'];
  render?: ButtonPrimitive.Props['render'];
  indicated?: boolean;
}

export function Button({
  variant,
  children,
  size,
  kind,
  indicated = false,
  pending,
  disabled,
  className,
  ...props
}: Props) {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-pending={pending ? '' : undefined}
      data-variant={variant}
      data-size={size}
      disabled={disabled || !!pending}
      className={cn(button({ variant, size, kind, className }))}
      data-indicated={indicated ? '' : undefined}
      {...props}
    >
      {children}
      {pending ? <Spinner kind="overlay" size={size} /> : null}
    </ButtonPrimitive>
  );
}
